import { useEffect } from 'react';
import './RockSmashAnimation.css';

/**
 * PROFESSIONAL ROCK SMASH ANIMATION
 * Fighting-type physical move with rock-breaking effects
 * 
 * Key Features:
 * - Fighting-type physical attack (40 base power, 100% accuracy)
 * - 50% chance to lower opponent's Defense stat
 * - Rock-breaking strike with debris and impact effects
 * - Professional gaming company quality with martial arts timing
 * - Physical move animation with contact and destruction mechanics
 */
export default function RockSmashAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate strike trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Fighting Stance Preparation (0-400ms)
    
    // Player enhancement with fighting aura
    playerEl.classList.add('rock-smash-user-charge');
    
    // Fighting energy aura around player
    const fightingAura = document.createElement('div');
    fightingAura.className = 'rock-smash-fighting-aura';
    fightingAura.style.left = `${playerX - 80}px`;
    fightingAura.style.top = `${playerY - 80}px`;
    container.appendChild(fightingAura);
    elements.push(fightingAura);

    // Power buildup particles around player
    for (let i = 0; i < 12; i++) {
      const powerParticle = document.createElement('div');
      powerParticle.className = 'rock-smash-power-particle';
      const angle = (i / 12) * 360;
      const radius = 50 + (i % 3) * 10;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      powerParticle.style.left = `${particleX - 3}px`;
      powerParticle.style.top = `${particleY - 3}px`;
      powerParticle.style.animationDelay = `${i * 30}ms`;
      powerParticle.textContent = '💥';
      container.appendChild(powerParticle);
      elements.push(powerParticle);
    }

    // Fighting spirit effects
    for (let i = 0; i < 8; i++) {
      const spirit = document.createElement('div');
      spirit.className = 'rock-smash-spirit-effect';
      const spiritAngle = (i / 8) * 360;
      const spiritRadius = 70;
      const spiritX = playerX + Math.cos(spiritAngle * Math.PI / 180) * spiritRadius;
      const spiritY = playerY + Math.sin(spiritAngle * Math.PI / 180) * spiritRadius;
      spirit.style.left = `${spiritX - 5}px`;
      spirit.style.top = `${spiritY - 5}px`;
      spirit.style.animationDelay = `${i * 45}ms`;
      container.appendChild(spirit);
      elements.push(spirit);
    }

    // PHASE 2: Strike Dash (400-700ms)
    const phase2Timer = setTimeout(() => {
      
      // Player dash movement toward opponent
      playerEl.classList.add('rock-smash-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.8}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.8}px`);

      // Fighting energy particles during dash
      for (let i = 0; i < 8; i++) {
        const energyTimer = setTimeout(() => {
          const energyParticle = document.createElement('div');
          energyParticle.className = 'rock-smash-dash-energy';
          const progress = i / 8;
          const energyX = playerX + (dx * 0.8 * progress) + (Math.random() - 0.5) * 20;
          const energyY = playerY + (dy * 0.8 * progress) + (Math.random() - 0.5) * 20;
          energyParticle.style.left = `${energyX - 3}px`;
          energyParticle.style.top = `${energyY - 3}px`;
          energyParticle.textContent = '⚡';
          container.appendChild(energyParticle);
          elements.push(energyParticle);
        }, i * 25);
        elements.push({ timer: energyTimer, type: 'timer' });
      }

      // Speed lines during dash
      for (let i = 0; i < 6; i++) {
        const speedTimer = setTimeout(() => {
          const speedLine = document.createElement('div');
          speedLine.className = 'rock-smash-speed-line';
          const lineX = playerX + (dx * 0.8 * (i / 6)) + (Math.random() - 0.5) * 40;
          const lineY = playerY + (dy * 0.8 * (i / 6)) + (Math.random() - 0.5) * 40;
          speedLine.style.left = `${lineX - 2}px`;
          speedLine.style.top = `${lineY - 15}px`;
          speedLine.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
          container.appendChild(speedLine);
          elements.push(speedLine);
        }, i * 30);
        elements.push({ timer: speedTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Rock Smash Impact (700-1200ms)
    const phase3Timer = setTimeout(() => {
      
      // Opponent hit reaction
      opponentEl.classList.add('rock-smash-opponent-hit');

      // Reset player position
      playerEl.classList.remove('rock-smash-user-dash');
      playerEl.classList.add('rock-smash-user-strike');

      // Create barrier/rock effect at opponent position
      const rockBarrier = document.createElement('div');
      rockBarrier.className = 'rock-smash-barrier';
      rockBarrier.style.left = `${opponentX - 60}px`;
      rockBarrier.style.top = `${opponentY - 60}px`;
      rockBarrier.textContent = '🪨';
      container.appendChild(rockBarrier);
      elements.push(rockBarrier);

      // Massive fighting impact explosion
      const fightingImpact = document.createElement('div');
      fightingImpact.className = 'rock-smash-fighting-impact';
      fightingImpact.style.left = `${opponentX - 100}px`;
      fightingImpact.style.top = `${opponentY - 100}px`;
      container.appendChild(fightingImpact);
      elements.push(fightingImpact);

      // Rock debris particles
      for (let i = 0; i < 20; i++) {
        const debris = document.createElement('div');
        debris.className = 'rock-smash-debris';
        const debrisAngle = (i / 20) * 360;
        const debrisRadius = 60 + Math.random() * 50;
        const debrisX = opponentX + Math.cos(debrisAngle * Math.PI / 180) * debrisRadius;
        const debrisY = opponentY + Math.sin(debrisAngle * Math.PI / 180) * debrisRadius;
        debris.style.left = `${debrisX - 4}px`;
        debris.style.top = `${debrisY - 4}px`;
        debris.style.animationDelay = `${i * 15}ms`;
        debris.style.setProperty('--scatter-x', `${Math.cos(debrisAngle * Math.PI / 180) * 40}px`);
        debris.style.setProperty('--scatter-y', `${Math.sin(debrisAngle * Math.PI / 180) * 40}px`);
        debris.textContent = Math.random() > 0.5 ? '🪨' : '💥';
        container.appendChild(debris);
        elements.push(debris);
      }

      // Impact shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'rock-smash-shockwave';
          shockwave.style.left = `${opponentX - 70}px`;
          shockwave.style.top = `${opponentY - 70}px`;
          shockwave.style.animationDelay = `${i * 80}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 100);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Fighting energy burst
      for (let i = 0; i < 12; i++) {
        const energyBurst = document.createElement('div');
        energyBurst.className = 'rock-smash-energy-burst';
        const burstAngle = (i / 12) * 360;
        const burstRadius = 80;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        energyBurst.style.left = `${burstX - 4}px`;
        energyBurst.style.top = `${burstY - 4}px`;
        energyBurst.style.animationDelay = `${i * 25}ms`;
        energyBurst.textContent = '⚡';
        container.appendChild(energyBurst);
        elements.push(energyBurst);
      }
    }, 700);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Defense Reduction Effect (1200-1600ms)
    const phase4Timer = setTimeout(() => {
      
      // Remove strike effects
      playerEl.classList.remove('rock-smash-user-charge', 'rock-smash-user-strike');
      
      // Defense reduction visual effects (50% chance)
      const defenseDebuff = document.createElement('div');
      defenseDebuff.className = 'rock-smash-defense-debuff';
      defenseDebuff.style.left = `${opponentX - 70}px`;
      defenseDebuff.style.top = `${opponentY - 70}px`;
      container.appendChild(defenseDebuff);
      elements.push(defenseDebuff);

      // Weakening cracks around opponent
      for (let i = 0; i < 8; i++) {
        const crack = document.createElement('div');
        crack.className = 'rock-smash-defense-crack';
        const angle = (i / 8) * 360;
        const radius = 50;
        const crackX = opponentX + Math.cos(angle * Math.PI / 180) * radius;
        const crackY = opponentY + Math.sin(angle * Math.PI / 180) * radius;
        crack.style.left = `${crackX - 3}px`;
        crack.style.top = `${crackY - 3}px`;
        crack.style.animationDelay = `${i * 40}ms`;
        crack.style.transform = `rotate(${angle}deg)`;
        container.appendChild(crack);
        elements.push(crack);
      }

      // Defense down indicator
      const defenseDown = document.createElement('div');
      defenseDown.className = 'rock-smash-defense-down';
      defenseDown.style.left = `${opponentX - 30}px`;
      defenseDown.style.top = `${opponentY - 80}px`;
      defenseDown.textContent = 'DEF ↓';
      container.appendChild(defenseDown);
      elements.push(defenseDown);
    }, 1200);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1600ms)
    const cleanupTimer = setTimeout(() => {
      
      // Remove all CSS classes
      playerEl.classList.remove('rock-smash-user-charge', 'rock-smash-user-dash', 'rock-smash-user-strike');
      opponentEl.classList.remove('rock-smash-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1600);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('rock-smash-user-charge', 'rock-smash-user-dash', 'rock-smash-user-strike');
      opponentEl.classList.remove('rock-smash-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
RockSmashAnimation.moveType = 'fighting';
